<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useRevendaStore } from '@/stores/revenda';
import { useNotificationStore } from '@/stores/notification';
import DesativarRevendaModal from '@/components/modals/DesativarRevendaModal.vue';
import StatusLigacaoRevendaModal from '@/components/modals/StatusLigacaoRevendaModal.vue';

// Initialize stores
const revendaStore = useRevendaStore();
const notificationStore = useNotificationStore();

// Local state
const loading = computed(() => revendaStore.loading);
const error = computed(() => revendaStore.error);
const revendas = computed(() => revendaStore.revendas);
const filtros = computed(() => revendaStore.filtros);
const showFilters = ref(true);

const pagination = computed(() => revendaStore.pagination);
const showNoResults = computed(() => !loading.value && revendas.value.length === 0);

const currentPage = ref(1);

const actionLoading = ref({});
const ligacaoLoading = ref({});

let searchTimeout = null;

const statusOptions = ref([
  { title: 'Todas', value: '' },
  { title: 'Ativas', value: 1 },
  { title: 'Inativas', value: 0 }
]);

// States list for filters
const estadosList = ref([
  { title: 'Todos', value: '' },
  { title: 'AC', value: 'AC' },
  { title: 'AL', value: 'AL' },
  { title: 'AP', value: 'AP' },
  { title: 'AM', value: 'AM' },
  { title: 'BA', value: 'BA' },
  { title: 'CE', value: 'CE' },
  { title: 'DF', value: 'DF' },
  { title: 'ES', value: 'ES' },
  { title: 'GO', value: 'GO' },
  { title: 'MA', value: 'MA' },
  { title: 'MT', value: 'MT' },
  { title: 'MS', value: 'MS' },
  { title: 'MG', value: 'MG' },
  { title: 'PA', value: 'PA' },
  { title: 'PB', value: 'PB' },
  { title: 'PR', value: 'PR' },
  { title: 'PE', value: 'PE' },
  { title: 'PI', value: 'PI' },
  { title: 'RJ', value: 'RJ' },
  { title: 'RN', value: 'RN' },
  { title: 'RS', value: 'RS' },
  { title: 'RO', value: 'RO' },
  { title: 'RR', value: 'RR' },
  { title: 'SC', value: 'SC' },
  { title: 'SP', value: 'SP' },
  { title: 'SE', value: 'SE' },
  { title: 'TO', value: 'TO' }
]);

const showCopyNotification = ref(false);
const copyMessage = ref('');

const copyToClipboard = async (data) => {
  try {
    // Handle both direct arguments and object format
    const text = data.text || 'Texto';
    const value = data.value || data;
    
    await navigator.clipboard.writeText(value);
    
    // Configurar e exibir o snackbar
    copyMessage.value = `${text} copiado com sucesso!`;
    showCopyNotification.value = true;
    
  } catch (err) {
    console.error('Erro ao copiar para área de transferência:', err);
    // Mostrar mensagem de erro em caso de falha
    copyMessage.value = 'Erro ao copiar para a área de transferência';
    showCopyNotification.value = true;
  }
};

const formatDateForApi = (dateStr) => {
  if (!dateStr) return '';
  
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
  
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  
  return dateStr;
};

const aplicarFiltrosComData = () => {
  if (filtros.value.data_inicial) {
    filtros.value.data_inicial = formatDateForApi(filtros.value.data_inicial);
  }
  if (filtros.value.data_final) {
    filtros.value.data_final = formatDateForApi(filtros.value.data_final);
  }
  
  currentPage.value = 1;
  revendaStore.aplicarFiltros();
};

const selectedFilterType = ref('nomefantasia');
const filterValue = ref('');

// Filter type options with proper field names and configurations
const filterTypeOptions = [
  { title: 'ID Revenda', value: 'idrevenda', icon: 'mdi-identifier', type: 'number' },
  { title: 'Nome Fantasia', value: 'nomefantasia', icon: 'mdi-store', type: 'text', maxLength: 255 },
  { title: 'Razão Social', value: 'razaosocial', icon: 'mdi-domain', type: 'text', maxLength: 255 },
  { title: 'Nome Responsável', value: 'responsavel', icon: 'mdi-account', type: 'text', maxLength: 255 },
  { title: 'Responsável Financeiro', value: 'responsavel_financeiro', icon: 'mdi-account-cash', type: 'text', maxLength: 255 },
  { title: 'Telefone/Celular', value: 'telefone', icon: 'mdi-phone', type: 'tel', mask: '(##) #####-####' },
  { title: 'Email', value: 'email', icon: 'mdi-email', type: 'email', maxLength: 255 },
  { title: 'CNPJ', value: 'cnpj', icon: 'mdi-file-document-outline', type: 'text', mask: '##.###.###/####-##' },
  { title: 'Código Ativação', value: 'codigo_ativacao', icon: 'mdi-key', type: 'text', maxLength: 255 },
  { title: 'Cidade', value: 'cidade', icon: 'mdi-city', type: 'text', maxLength: 100 },
  { title: 'Estado', value: 'estado', icon: 'mdi-map-marker', type: 'text', maxLength: 2 },
  { title: 'ID Revendedor', value: 'idrevendedor', icon: 'mdi-tag', type: 'number' },
  { title: 'Cadastrador', value: 'cadastrador', icon: 'mdi-account-plus', type: 'number' },
];

// Helper functions for input types, max length and masks
const getInputType = (filterType) => {
  const option = filterTypeOptions.find(opt => opt.value === filterType);
  return option?.type || 'text';
};

const getMaxLength = (filterType) => {
  const option = filterTypeOptions.find(opt => opt.value === filterType);
  return option?.maxLength;
};

const getInputMask = (filterType) => {
  const option = filterTypeOptions.find(opt => opt.value === filterType);
  return option?.mask;
};

const handleSearch = () => {
  if (searchTimeout) clearTimeout(searchTimeout);
  
  searchTimeout = setTimeout(() => {
    // Reset all filter values first
    filtros.value.idrevenda = '';
    filtros.value.idrevendedor = '';
    filtros.value.nomefantasia = '';
    filtros.value.cidade = '';
    filtros.value.estado = '';
    filtros.value.razaosocial = '';
    filtros.value.responsavel = '';
    filtros.value.responsavel_financeiro = '';
    filtros.value.telefone = '';
    filtros.value.email = '';
    filtros.value.cnpj = '';
    filtros.value.codigo_ativacao = '';
    filtros.value.cadastrador = '';
    
    // Set only the selected filter
    if (filterValue.value && filterValue.value.trim()) {
      filtros.value[selectedFilterType.value] = filterValue.value.trim();
    }
    
    currentPage.value = 1;
    revendaStore.aplicarFiltros();
  }, 500);
};

watch(selectedFilterType, (newFilterType, oldFilterType) => {
  if (oldFilterType && filterValue.value && filterValue.value.trim()) {
    filtros.value[oldFilterType] = '';
    handleSearch();
  }
});

const limparFiltros = () => {
  currentPage.value = 1;
  filterValue.value = '';
  selectedFilterType.value = 'nomefantasia';
  revendaStore.limparFiltros();
};

const deactivateModalOpen = ref(false);
const selectedRevenda = ref({
  id: null,
  nomefantasia: '',
  ativo: 0
});

const statusLigacaoRevendaModalOpen = ref(false);
const selectedRevendaLigacao = ref({
  id: null,
  nomefantasia: '',
  ligacao_habilitada: false
});

const toggleStatus = async (revenda) => {
  if (actionLoading.value[revenda.id]) return;
  
  const isActivating = revenda.ativo === 0 || revenda.ativo === '0';
  
  if (isActivating) {
    // Set loading state for this action
    actionLoading.value = { 
      ...actionLoading.value, 
      [revenda.id]: true 
    };
    
    try {
      const response = await revendaStore.ativarRevenda(revenda.id);
      
      if (response.success) {
        // Update the local state without a full refresh
        revenda.ativo = 1;
        
        // Use notification system
        notificationStore.showNotification(response.message || 'Revenda ativada com sucesso', 'success');
      } else {
        // Use notification system
        notificationStore.showNotification(response.message || 'Erro ao ativar revenda', 'error');
      }
    } catch (error) {
      console.error('Erro ao ativar revenda:', error);
      notificationStore.showNotification('Ocorreu um erro ao processar esta ação', 'error');
    } finally {
      // Clear loading state
      const { [revenda.id]: _, ...remaining } = actionLoading.value;
      actionLoading.value = remaining;
    }
  } else {
    // Desativar - show modal - clone the object to avoid reactivity issues
    selectedRevenda.value = { ...revenda };
    deactivateModalOpen.value = true;
  }
};

// Handle revenda deactivation (from modal)
const handleRevendaDeactivated = (updatedRevenda) => {
  const index = revendas.value.findIndex(r => r.id === updatedRevenda.id);
  if (index !== -1) {
    revendas.value[index].ativo = updatedRevenda.ativo;
  }
};

const alterarStatusLigacao = (revenda) => {
  if (ligacaoLoading.value[revenda.id]) return;
  
  selectedRevendaLigacao.value = { ...revenda };
  statusLigacaoRevendaModalOpen.value = true;
};

const handleRevendaLigacaoUpdated = (updatedRevenda) => {
  const index = revendas.value.findIndex(r => r.id === updatedRevenda.id);
  if (index !== -1) {
    revendas.value[index].ligacao_habilitada = updatedRevenda.ligacao_habilitada;
  }
};

const handleLigacaoLoadingState = ({ revendaId, loading }) => {
  if (loading) {
    ligacaoLoading.value = { 
      ...ligacaoLoading.value, 
      [revendaId]: true 
    };
  } else {
    const { [revendaId]: _, ...remaining } = ligacaoLoading.value;
    ligacaoLoading.value = remaining;
  }
};

// Secret usado para gerar o token (use VITE_REVENDER_SECRET em ambiente ou substitua pelo secret real)
const revendedorSecret = import.meta.env.VITE_REVENDER_SECRET || 'autenticacao-revendedor';

const textToUint8Array = (text) => new TextEncoder().encode(text);

const bufferToHex = (buffer) =>
  Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

// Gera um token baseado no id do revendedor + secret + minuto anterior (Web Crypto API)
const generateToken = async (idRevendedor) => {
  const prevMinute = Math.floor(Date.now() / 1000 / 60) - 1;
  const str = `${idRevendedor}${revendedorSecret}${prevMinute}`;

  if (crypto?.subtle && typeof crypto.subtle.digest === 'function') {
    const data = textToUint8Array(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return bufferToHex(hashBuffer);
  }

  console.warn('Web Crypto API não disponível — gere o token no backend ou use crypto-es como fallback.');
  return '';
};

// Function to access revendedor account (abre em nova aba e inclui token)
const acessarConta = async (id) => {
  if (!id) return;

  try {
    const token = await generateToken(id);
    if (!token) {
      notificationStore.showNotification('Erro ao gerar token de acesso', 'error');
      return;
    }

    const url = `https://revendedor.precodogas.com.br/autenticar/externo?revendedor_id=${id}&token=${token}`;
    window.open(url, '_blank');
  } catch (err) {
    console.error('Erro ao acessar conta:', err);
    notificationStore.showNotification('Erro ao abrir conta do revendedor', 'error');
  }
};

// Initial data fetch 
onMounted(() => {
  limparFiltros();
});

// Update the onPageChange method to scroll to top after page change
const onPageChange = (newPage) => {
  if (currentPage.value !== newPage) {
    currentPage.value = newPage;
    revendaStore.buscarRevendasPage(newPage).then(() => {
      // Scroll to top of the page after data is loaded
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }
};
</script>

<template>
  <div>
    <v-snackbar
      v-model="showCopyNotification"
      :timeout="2000"
      color="success"
      location="top"
    >
      {{ copyMessage }}
    </v-snackbar>

    <v-row>
      <v-col cols="12" class="d-flex align-center">
        <h1 class="text-h4">Gerenciar Revendas</h1>
        <v-spacer></v-spacer>
        <v-btn
          icon
          variant="text"
          color="primary"
          @click="showFilters = !showFilters"
          class="mr-2"
        >
          <v-icon>mdi-filter-variant</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <!-- Filters section -->
    <v-expand-transition>
      <v-card class="mb-4" v-if="showFilters">
        <v-card-text>
          <v-row>
            <!-- Data inicial filter -->
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filtros.data_inicial"
                label="Data Inicial"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                clearable
                type="date"
                prepend-inner-icon="mdi-calendar-start"
              ></v-text-field>
            </v-col>
            
            <!-- Data final filter -->
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filtros.data_final"
                label="Data Final"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                clearable
                type="date"
                prepend-inner-icon="mdi-calendar-end"
                :min="filtros.data_inicial"
              ></v-text-field>
            </v-col>

            <!-- Ativo filter -->
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filtros.ativo"
                :items="statusOptions"
                item-title="title"
                item-value="value"
                label="Status revenda"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                prepend-inner-icon="mdi-checkbox-marked-circle-outline"
              ></v-select>
            </v-col>
            
            <!-- Estado filter -->
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filtros.estado"
                :items="estadosList"
                item-title="title"
                item-value="value"
                label="Estado"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                prepend-inner-icon="mdi-map-marker"
              ></v-select>
            </v-col>
            
            <!-- Gás do Povo filter -->
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filtros.gas_do_povo"
                :items="[
                  { title: 'Todos', value: '' },
                  { title: 'Participantes', value: 1 },
                  { title: 'Não Participantes', value: 0 }
                ]"
                item-title="title"
                item-value="value"
                label="Gás do Povo"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                prepend-inner-icon="mdi-gas-cylinder"
              ></v-select>
            </v-col>
          </v-row>

          <v-row>
            <!-- Replace the three separate inputs with select + input combination -->
            <v-col cols="12" sm="6" md="4">
              <v-select
                v-model="selectedFilterType"
                :items="filterTypeOptions"
                item-title="title"
                item-value="value"
                label="Filtrar por"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
              >
                <template v-slot:prepend-inner>
                  <v-icon v-for="option in filterTypeOptions" 
                         :key="option.value" 
                         v-show="selectedFilterType === option.value">
                    {{ option.icon }}
                  </v-icon>
                </template>
              </v-select>
            </v-col>
            
            <v-col cols="12" sm="6" md="8">
              <v-text-field
                v-model="filterValue"
                :label="filterTypeOptions.find(opt => opt.value === selectedFilterType)?.title || 'Valor'"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                clearable
                :type="getInputType(selectedFilterType)"
                :maxlength="getMaxLength(selectedFilterType)"
                :v-mask="getInputMask(selectedFilterType)"
                @update:model-value="handleSearch"
                @keyup.enter="revendaStore.aplicarFiltros()"
                :prepend-inner-icon="filterTypeOptions.find(opt => opt.value === selectedFilterType)?.icon"
              ></v-text-field>
            </v-col>
          </v-row>
          
          <v-row class="mt-2">
            <v-col cols="12" class="d-flex">
              <v-spacer></v-spacer>
              
              <v-btn
                color="primary"
                variant="text"
                class="mr-2"
                prepend-icon="mdi-refresh"
                @click="limparFiltros"
              >
                Limpar Filtros
              </v-btn>
              
              <v-btn
                color="primary"
                prepend-icon="mdi-magnify"
                @click="aplicarFiltrosComData"
              >
                Pesquisar
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-expand-transition>

    <v-alert v-if="error" type="error" closable class="mb-4">
      {{ error }}
    </v-alert>

    <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

    <!-- Data table section -->
    <v-data-table
      :headers="[
        { title: 'Nº', value: 'id', width: '60px' },
        { title: 'Nome Fantasia', value: 'nomefantasia' },
        { title: 'Revendedor', value: 'revendedor', },
        { title: 'Localização', value: 'cidade', },
        { title: 'Contatos', value: 'contato', },
        { title: 'CNPJ', value: 'cnpj',  },
        { title: 'Cadastro', value: 'dtcadastro',  },
        { title: 'Status', value: 'ativo',  },
        { title: 'Gás do Povo', value: 'gas_do_povo',  },
        { title: 'Ligações', value: 'ligacao_habilitada',  },
        { title: 'Acesso', value: 'acessar_conta', sortable: false, },
        { title: 'Ações', value: 'actions', sortable: false, }
      ]"
      :items="revendas"
      :items-per-page="pagination.perPage"
      :loading="loading"
      class="custom-table"
    >
      <template #item="{ item }">
        <tr>
          <td>{{ item.id }}</td>
          <td>{{ item.nomefantasia || '-' }}</td>
          <td>
            <div class="text-subtitle-2">
              <v-icon size="small" color="primary" title="Editar Revendedor"
                @click="$router.push({ name: 'revendedor-detalhes', params: { id: item.revendedor?.id } })">mdi-pencil</v-icon> 
              {{ item.revendedor?.razaosocial || '-' }}</div>
            <div class="text-caption" title="Responsável">
              <v-icon size="x-small" color="grey">mdi-account</v-icon> 
              {{ item.revendedor?.responsavel || '-' }}
            </div>
            <div v-if="item.revendedor?.responsavel_financeiro"  title="Responsável Financeiro" class="text-caption">
              <v-icon size="x-small" color="grey">mdi-account-cash</v-icon> 
              {{ item.revendedor.responsavel_financeiro }}
            </div>
          </td>
          <td>
            <div>{{ item.cidade || '-' }}</div>
            <div class="text-caption">{{ item.estado || '-' }}</div>
          </td>
          <td>
            <div class="mb-2" v-if="item.revendedor?.telefone1">
              <v-icon @click="copyToClipboard({ text: 'telefone', value: item.telefone1 })" size="small" color="primary" class="mr-1">mdi-phone</v-icon>
              {{ item.revendedor.telefone1 }}
            </div>
            <div v-if="item.revendedor?.celular">
              <v-icon @click="copyToClipboard({ text: 'celular', value: item.celular })" size="small" color="primary" class="mr-1">mdi-cellphone</v-icon>
              {{ item.revendedor.celular }}
            </div>
          </td>
          <td>{{ item.revendedor?.cnpj || '-' }}</td>
          <td>{{ item.dtcadastro || '-' }}</td>
          <td>
            <v-chip
              :color="item.ativo === '1' || item.ativo === 1 ? 'success' : 'error'"
            >
              {{ item.ativo === '1' || item.ativo === 1 ? 'Ativa' : 'Inativa' }}
            </v-chip>
          </td>
          <td>
            <v-chip
              v-if="item.gas_do_povo_status === 1"
              color="orange"
              variant="flat"
              size="small"
            >
              <v-icon size="small" class="mr-1">mdi-gas-cylinder</v-icon>
              Gás do Povo
            </v-chip>
            <span v-else class="text-grey">-</span>
          </td>
          <td>
            <v-chip
              :color="!item.ligacao_habilitada ? 'success' : 'error'"
              variant="outlined"
              @click="alterarStatusLigacao(item)"
              :disabled="ligacaoLoading[item.id]"
              class="cursor-pointer"
              :class="{ 'opacity-50': ligacaoLoading[item.id] }"
              :title="!item.ligacao_habilitada ? 'Essa revenda não está recebendo ligações' : 'Essa revenda está recebendo ligações'"
            >
              <v-icon size="small" :icon="!item.ligacao_habilitada ? 'mdi-phone' : 'mdi-phone-off'" class="mr-1"></v-icon>
              {{ !item.ligacao_habilitada ? 'Receber Ligação' : 'Não receber ligação' }}
              <v-progress-circular
                v-if="ligacaoLoading[item.id]"
                indeterminate
                size="16"
                width="2"
                class="ml-2"
              ></v-progress-circular>
            </v-chip>
          </td>
          <td>
            <v-btn size="small" prepend-icon="mdi-login" @click="acessarConta(item.revendedor?.id)">Entrar</v-btn>
          </td>
          <td>
            <v-tooltip text="Desativar Revenda" v-if="item.ativo === '1' || item.ativo === 1">
              <template v-slot:activator="{ props }">
                <v-icon
                  color="error"
                  icon="mdi-close-circle-outline"
                  @click="toggleStatus(item)"
                  :loading="actionLoading[item.id]"
                  class="mr-2"
                  v-bind="props"
                ></v-icon>
              </template>
            </v-tooltip>
            
            <v-tooltip text="Ativar Revenda" v-else>
              <template v-slot:activator="{ props }">
                <v-icon 
                  size="small"
                  color="success"
                  icon="mdi-check-circle-outline"
                  @click="toggleStatus(item)"
                  :loading="actionLoading[item.id]"
                  class="mr-2"
                  v-bind="props"
                ></v-icon>
              </template>
            </v-tooltip>
            
            <v-tooltip text="Editar Revenda">
              <template v-slot:activator="{ props }">
                <v-icon 
                  class="ml-2"
                  color="primary"
                  icon="mdi-pencil"
                  @click="$router.push({ name: 'revenda-detalhes', params: { id: item.id } })"
                  v-bind="props"
                ></v-icon>
              </template>
            </v-tooltip>
          </td>
        </tr>
      </template>

      <!-- Bottom pagination -->
      <template #bottom>
        <div class="d-flex justify-space-between align-center px-4 py-2">
          <span class="text-caption text-secondary">
            Total de {{ pagination.total }} revenda{{ pagination.total !== 1 ? 's' : '' }}
          </span>

          <v-pagination
            v-if="pagination.lastPage > 1"
            :model-value="currentPage"
            :length="pagination.lastPage"
            :total-visible="5"
            @update:model-value="onPageChange"
            rounded
            class="my-2"
          ></v-pagination>
        </div>
      </template>
    </v-data-table>
    
    <!-- Empty state -->
    <v-card
      v-if="showNoResults"
      class="text-center py-8"
    >
      <v-icon size="64" color="grey" class="mb-4">mdi-store-off</v-icon>
      <h3 class="text-h5 mb-2">Nenhuma revenda encontrada</h3>
      <p class="text-body-1 text-grey mb-4">
        Tente ajustar seus filtros ou limpar a pesquisa.
      </p>
      <v-btn
        color="primary"
        prepend-icon="mdi-refresh"
        @click="limparFiltros"
      >
        Limpar filtros
      </v-btn>
    </v-card>
    
    <!-- Loading state -->
    <v-card
      v-if="loading && revendas.length === 0"
      class="text-center py-8"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
        class="mb-4"
      ></v-progress-circular>
      <h3 class="text-h5 mb-2">Carregando revendas...</h3>
    </v-card>
    
    <!-- Desativar Revenda Modal -->
    <DesativarRevendaModal
      v-if="selectedRevenda"
      v-model:show="deactivateModalOpen"
      :revenda="selectedRevenda"
      @desativado="handleRevendaDeactivated"
    />
    
    <!-- alterar status ligação revenda modal -->
    <StatusLigacaoRevendaModal
      v-if="selectedRevendaLigacao"
      v-model:show="statusLigacaoRevendaModalOpen"
      :revenda="selectedRevendaLigacao"
      @updated="handleRevendaLigacaoUpdated"
      @loading-state="handleLigacaoLoadingState"
    />
  </div>
</template>

<style scoped>
/* Using the same styling as in other tables */
.custom-table :deep(.v-data-table__th) {
  white-space: nowrap;
  text-transform: uppercase;
  font-size: 0.8rem;
}

.custom-table :deep(.v-data-table__tr:hover) {
  background-color: rgba(var(--v-theme-primary), 0.05) !important;
}

.cursor-pointer {
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.cursor-pointer:hover {
  opacity: 0.8;
}
</style>
